import { createListCollection } from "@ark-ui/react/select";
import { Selector } from "@base/ui/Selector";
import { TextField } from "@base/ui/TextField";
import type { MemberStatus, MemberType } from "@features/admin/model/types";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const ALL = "ALL";

interface FilterOption {
  value: string;
  label: string;
}

export interface MemberFiltersProps {
  search: string;
  type?: MemberType;
  status?: MemberStatus;
  onSearchChange: (value: string) => void;
  onTypeChange: (value?: MemberType) => void;
  onStatusChange: (value?: MemberStatus) => void;
}

export const MemberFilters = ({
  search,
  type,
  status,
  onSearchChange,
  onTypeChange,
  onStatusChange,
}: MemberFiltersProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(search);
  // 디바운스로 올린 검색어가 URL을 거쳐 다시 내려오는 동안에도 사용자는 계속 타이핑한다.
  // 우리가 올린 값인지 기억해두고, 그 값이 되돌아왔을 때는 입력을 덮어쓰지 않는다.
  const emittedRef = useRef(search);
  // 부모가 매 렌더 새 콜백을 넘겨도 디바운스 타이머가 리셋되지 않도록 최신 콜백만 참조한다.
  const onSearchChangeRef = useRef(onSearchChange);

  useEffect(() => {
    onSearchChangeRef.current = onSearchChange;
  }, [onSearchChange]);

  // URL이 외부에서 바뀐 경우(뒤로가기 등)에만 입력값을 맞춘다.
  useEffect(() => {
    if (search === emittedRef.current) return;
    emittedRef.current = search;
    setDraft(search);
  }, [search]);

  useEffect(() => {
    if (draft === emittedRef.current) return;
    const timeoutId = window.setTimeout(() => {
      emittedRef.current = draft;
      onSearchChangeRef.current(draft);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [draft]);

  const typeCollection = useMemo(
    () =>
      createListCollection<FilterOption>({
        items: [
          { value: ALL, label: "All" },
          { value: "SILVER_MONTHLY", label: t("memberType_SILVER_MONTHLY") },
          { value: "SILVER_YEARLY", label: t("memberType_SILVER_YEARLY") },
          { value: "VIRTUAL_ADDRESS_MONTHLY", label: t("memberType_VIRTUAL_ADDRESS_MONTHLY") },
          { value: "VIRTUAL_ADDRESS_YEARLY", label: t("memberType_VIRTUAL_ADDRESS_YEARLY") },
          { value: "DAY_PASS", label: t("memberType_DAY_PASS") },
        ],
      }),
    [t],
  );

  const statusCollection = useMemo(
    () =>
      createListCollection<FilterOption>({
        items: [
          { value: ALL, label: "All" },
          { value: "ACTIVE", label: t("memberStatus_ACTIVE") },
          { value: "CANCELLED", label: t("memberStatus_EXPIRED") },
        ],
      }),
    [t],
  );

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <TextField className="w-full md:w-[320px]">
        <TextField.Input
          size="medium"
          variant="outline"
          placeholder={t("adminSearchPlaceholder")}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          prefix={<Search size={16} className="text-gray-500" />}
        />
      </TextField>

      <Selector<FilterOption>
        collection={typeCollection}
        value={[type ?? ALL]}
        onValueChange={(details) => {
          const next = details.value[0];
          onTypeChange(next === ALL ? undefined : (next as MemberType));
        }}
        className="w-[160px]"
      >
        <Selector.Control size="medium" variant="outline" aria-label={t("adminFilterType")}>
          {({ selectedItems }) => {
            // 목록에는 "All"로 노출하지만, 필터 미적용 상태의 트리거에는 필터 이름(Type)을 보여준다.
            const selected = selectedItems[0] as FilterOption | undefined;
            return (
              <span className="truncate">
                {selected && selected.value !== ALL ? selected.label : t("adminFilterType")}
              </span>
            );
          }}
        </Selector.Control>
        <Selector.Content className="overflow-y-auto z-10 p-2 max-h-60" usePortal={false}>
          {typeCollection.items.map((item) => (
            <Selector.Item key={item.value} item={item} className="px-3 py-2 cursor-pointer hover:bg-gray-100">
              <span className="text-gray-1000 text-label2">{item.label}</span>
            </Selector.Item>
          ))}
        </Selector.Content>
      </Selector>

      <Selector<FilterOption>
        collection={statusCollection}
        value={[status ?? ALL]}
        onValueChange={(details) => {
          const next = details.value[0];
          onStatusChange(next === ALL ? undefined : (next as MemberStatus));
        }}
        className="w-[160px]"
      >
        <Selector.Control size="medium" variant="outline" aria-label={t("adminFilterStatus")}>
          {({ selectedItems }) => {
            // 목록에는 "All"로 노출하지만, 필터 미적용 상태의 트리거에는 필터 이름(Status)을 보여준다.
            const selected = selectedItems[0] as FilterOption | undefined;
            return (
              <span className="truncate">
                {selected && selected.value !== ALL ? selected.label : t("adminFilterStatus")}
              </span>
            );
          }}
        </Selector.Control>
        <Selector.Content className="overflow-y-auto z-10 p-2 max-h-60" usePortal={false}>
          {statusCollection.items.map((item) => (
            <Selector.Item key={item.value} item={item} className="px-3 py-2 cursor-pointer hover:bg-gray-100">
              <span className="text-gray-1000 text-label2">{item.label}</span>
            </Selector.Item>
          ))}
        </Selector.Content>
      </Selector>
    </div>
  );
};
